import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminCompaniesRoster } from '@/api/(admin)/companies';
import type { AdminCompaniesQueryInput } from '../-types';
import {
  buildAdminCompaniesQs,
  toAdminCompaniesParams,
} from '../-utils/listParams';

/**
 * Shared by the route loader and the component's hook.
 *
 * The key is the *converted* params rather than the raw input, so the loader's
 * URL-shaped values and `useTable`'s state-shaped values land on the same cache
 * entry — otherwise the prefetch warms a key nothing reads and the page still
 * flashes a spinner.
 *
 * The tab still rides in the key, though it is no longer two endpoints: it now
 * decides only the `removed` query flag, and the active/removed rosters must
 * still land on separate cache entries.
 */
export function adminCompaniesQueryOptions(input: AdminCompaniesQueryInput) {
  const params = toAdminCompaniesParams(input);
  const qs = buildAdminCompaniesQs(params);
  const removed = input.tab === 'removed';
  return queryOptions({
    queryKey: ['admin', 'companies', 'list', input.tab, params] as const,
    queryFn: () => fetchAdminCompaniesRoster(qs, removed),
    staleTime: 60_000,
  });
}

/** Everything under this prefix — what the write paths invalidate. */
export const adminCompaniesQueryKey = ['admin', 'companies'] as const;

export function useAdminCompanies(input: AdminCompaniesQueryInput) {
  const query = useQuery(adminCompaniesQueryOptions(input));

  return {
    ...query,
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata?.pagination?.total ?? 0,
  };
}
